import React, { useMemo, useState } from "react";
import { Car, ChevronRight } from "lucide-react";
import { ARAC_GORSEL, gorselFromDiagram } from "./aracZones";

const GRUP_RENK = {
    on: { fill: "rgba(52, 211, 153, 0.45)", stroke: "#6ee7b7", aktif: "rgba(52, 211, 153, 0.72)" },
    yan: { fill: "rgba(96, 165, 250, 0.4)", stroke: "#93c5fd", aktif: "rgba(96, 165, 250, 0.68)" },
    arka: { fill: "rgba(167, 139, 250, 0.4)", stroke: "#c4b5fd", aktif: "rgba(167, 139, 250, 0.68)" },
    diger: { fill: "rgba(251, 191, 36, 0.38)", stroke: "#fcd34d", aktif: "rgba(251, 191, 36, 0.65)" },
};

function grupBul(id) {
    if (id.startsWith("on_") || id.includes("far") || (id.includes("cam") && id.includes("on"))) return "on";
    if (id.startsWith("arka_") || id.includes("bagaj")) return "arka";
    if (id.includes("kapi") || id.includes("camurluk") || id.includes("ayna")) return "yan";
    return "diger";
}

export { grupBul };

export default function AracDiagram({
    zones,
    secili,
    onToggle,
    onHover,
    disabled,
    diagramMeta,
    gorunumler,
    aktifGorunum,
    onGorunumChange,
}) {
    const [hoverId, setHoverId] = useState(null);
    const gorunum = aktifGorunum || "ustten";
    const gorunumMeta = (gorunumler || []).find((g) => g.id === gorunum) || gorunumler?.[0];
    const gorsel = gorselFromDiagram(gorunumMeta?.gorsel ? { gorsel: gorunumMeta.gorsel } : diagramMeta);
    const grupFiltre = gorunumMeta?.grup_filtre;

    const seciliMap = useMemo(() => {
        const m = new Map();
        (secili || []).forEach((p) => m.set(p.diagram_id, p));
        return m;
    }, [secili]);

    const merged = useMemo(
        () =>
            (zones || [])
                .filter((z) => z.x != null && z.y != null)
                .filter((z) => {
                    if (!grupFiltre?.length) return true;
                    const g = grupBul(z.id);
                    return grupFiltre.includes(g);
                })
                .sort((a, b) => (a.etiket || "").localeCompare(b.etiket || "", "tr")),
        [zones, grupFiltre],
    );

    const handleZone = (z) => {
        if (disabled) return;
        onToggle({
            id: z.id,
            etiket: z.etiket,
            varsayilan_islem: z.varsayilan_islem,
        });
    };

    return (
        <div className="w-full max-w-lg mx-auto">
            {gorunumler?.length > 1 ? (
                <div className="flex flex-wrap gap-1.5 mb-3 justify-center">
                    {gorunumler.map((g) => (
                        <button
                            key={g.id}
                            type="button"
                            onClick={() => onGorunumChange?.(g.id)}
                            className={`px-2.5 py-1 rounded-full text-[10px] border transition-colors ${
                                gorunum === g.id
                                    ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200"
                                    : "border-white/10 text-slate-500 hover:border-white/20"
                            }`}
                        >
                            {g.etiket}
                        </button>
                    ))}
                </div>
            ) : null}
            <div
                className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0a0e14] shadow-inner"
                style={{ aspectRatio: gorsel.aspectRatio || "1011 / 470" }}
            >
                <img
                    src={gorsel.url}
                    alt="Araç üstten görünüm"
                    className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                    draggable={false}
                />

                <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-0.5 text-[9px] text-emerald-400/90 font-medium">
                    <span>ÖN</span>
                    <ChevronRight className="h-3 w-3" />
                </div>

                <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    className="absolute inset-0 w-full h-full"
                    aria-hidden={false}
                    role="group"
                    aria-label="Hasarlı parça seçim alanları"
                >
                    {merged.map((z) => {
                        const aktif = seciliMap.has(z.id);
                        const hover = hoverId === z.id;
                        const grup = grupBul(z.id);
                        const renk = GRUP_RENK[grup] || GRUP_RENK.diger;
                        const x = Number(z.x);
                        const y = Number(z.y);
                        const w = Number(z.w);
                        const h = Number(z.h);

                        return (
                            <rect
                                key={z.id}
                                x={x}
                                y={y}
                                width={w}
                                height={h}
                                rx={1.2}
                                fill={aktif ? renk.aktif : hover ? renk.fill : "rgba(15,23,42,0.12)"}
                                stroke={aktif ? "#D4AF37" : hover ? renk.stroke : "rgba(100,116,139,0.35)"}
                                strokeWidth={aktif ? 0.55 : hover ? 0.4 : 0.2}
                                className={disabled ? "" : "cursor-pointer"}
                                style={{
                                    transition: "fill 0.12s, stroke 0.12s",
                                    pointerEvents: disabled ? "none" : "all",
                                }}
                                onClick={() => handleZone(z)}
                                onMouseEnter={() => {
                                    setHoverId(z.id);
                                    onHover?.(z);
                                }}
                                onMouseLeave={() => {
                                    setHoverId(null);
                                    onHover?.(null);
                                }}
                            />
                        );
                    })}
                </svg>

                {(hoverId || seciliMap.size > 0) && (
                    <div className="absolute bottom-0 inset-x-0 z-20 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 py-2 pointer-events-none">
                        <p className="text-[11px] text-center text-slate-300 truncate">
                            {hoverId
                                ? merged.find((z) => z.id === hoverId)?.etiket
                                : `${seciliMap.size} parça seçili`}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap justify-center gap-2 mt-3 text-[10px] text-slate-500">
                {Object.entries(GRUP_RENK).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1">
                        <span
                            className="w-2.5 h-2.5 rounded-sm border border-white/10"
                            style={{ background: v.fill }}
                        />
                        {k === "on" ? "Ön" : k === "yan" ? "Yan" : k === "arka" ? "Arka" : "Diğer"}
                    </span>
                ))}
                <span className="inline-flex items-center gap-1 text-[#D4AF37]/80">
                    <Car className="h-3 w-3" />
                    Fotoğraf üzeri işaretleme
                </span>
            </div>
            <p className="text-[9px] text-slate-600 text-center mt-1.5 leading-snug">{gorsel.kaynak}</p>
        </div>
    );
}

export { ensureAllZones } from "./aracZones";
